/**
 * Show, don't tell — géneros textuales en atlas/guías de humanidades.
 * Evita envolver explicaciones teóricas con estilos de carta, noticia o poema.
 */

import type { StimulusTheme } from '../types/payloads/stimulus.types';
import type { GuideBlockLike } from './humanitiesGuideEnforcer';
import { isHumanitiesFormulaSubject } from '../../activities/guide_editorial/guide_editorial.logic';
import { inferStimulusTheme } from './stimulusTheme';

const THEORETICAL_GENRE_RE =
  /\b(?:toda|todo|toda la|toda una)\s+(?:carta|noticia|nota|poema)\b|\b(?:sigue|siguen|presenta|presentan)\s+(?:una\s+)?estructura\b|\b(?:se\s+compone|est[aá]\s+formad[oa]|debe\s+incluir|elementos?\s+de\s+una?)\b|\b(?:partes?\s+(?:de\s+la|de\s+un[oa]?)|estructura\s+(?:de\s+la|de\s+un|cl[aá]sica|b[aá]sica))\b|\b(?:consiste\s+en|se\s+caracteriza\s+por)\b/i;

const LETTER_SALUTATION_RE = /\b(querid[oa]|estimad[oa]|apreciad[oa])\s+[\wáéíóúñ]/i;
const LETTER_CLOSING_RE =
  /\b(atentamente|saludos cordiales|de mi consideraci[oó]n|con cariño|con afecto|tu (?:hij[oa]|sobrin[oa]|amig[oa]))\b/i;
const LETTER_DATE_RE =
  /^(?:[A-ZÁÉÍÓÚÑ][\wáéíóúñ\s,.-]+,\s*)?\d{1,2}\s+de\s+[a-záéíóúñ]+\s+de\s+\d{4}/i;

const POEM_LINE_BREAKS_RE = /\n.{8,}\n/s;

export function isTheoreticalGenreExplanation(text: string): boolean {
  const blob = String(text || '').trim();
  if (!blob) return false;
  return THEORETICAL_GENRE_RE.test(blob);
}

export function isAuthenticLetterSample(content: string): boolean {
  const blob = String(content || '').trim();
  if (!blob || isTheoreticalGenreExplanation(blob)) return false;
  const firstLine = blob.split('\n')[0]?.trim() ?? '';
  return (
    LETTER_SALUTATION_RE.test(blob) ||
    LETTER_CLOSING_RE.test(blob) ||
    LETTER_DATE_RE.test(firstLine) ||
    /\b(te escribo|le escribo)\b/i.test(blob)
  );
}

export function isAuthenticNewspaperSample(content: string): boolean {
  const blob = String(content || '').trim();
  if (!blob || isTheoreticalGenreExplanation(blob)) return false;
  // Muestras escolares etiquetadas (Epígrafe/Titular/…) cuentan como noticia real, no teoría.
  if (
    /\b(ep[ií]grafe|titular|bajada|lead)\s*:/i.test(blob) &&
    blob.length >= 40 &&
    !/\b(?:toda|todo)\s+(?:carta|noticia)/i.test(blob)
  ) {
    return true;
  }
  const hasNewsCue = /\b(ayer|hoy|anoche|esta\s+mañana|pasado\s+\d{1,2}\s+de)\b/i.test(blob);
  const hasEventVerb =
    /\b(seg[uú]n|inform[oó]|report[oó]|ocurri[oó]|afect[oó]|declar[oó]|anunci[oó]|detuvieron|descubrieron|realiz[oó]|participaron|gan[oó]|presentaron)\b/i.test(
      blob,
    );
  const hasNewsTopic = /\b(incendio|accidente|terremoto|elecciones|gobierno|alcalde|parque|comuna|ciudad|colegio|feria|concurso|estudiante)/i.test(
    blob,
  );
  return (hasNewsCue && (hasEventVerb || hasNewsTopic)) || (hasEventVerb && hasNewsTopic && blob.length >= 30);
}

export function isAuthenticPoemSample(content: string): boolean {
  const blob = String(content || '').trim();
  if (!blob || isTheoreticalGenreExplanation(blob)) return false;
  const lines = blob.split('\n').map((l) => l.trim()).filter(Boolean);
  if (lines.length < 2) return false;
  return POEM_LINE_BREAKS_RE.test(blob) || lines.some((line) => /\b(rima|verso|estrofa)\b/i.test(line) === false && line.length < 60 && /[,;]$/.test(line));
}

export function isAuthenticGenreSample(content: string, theme: StimulusTheme): boolean {
  switch (theme) {
    case 'letter':
      return isAuthenticLetterSample(content);
    case 'newspaper':
      return isAuthenticNewspaperSample(content);
    case 'poem':
      return isAuthenticPoemSample(content);
    case 'document':
      return !isTheoreticalGenreExplanation(content) && content.trim().length >= 80;
    default:
      return !isTheoreticalGenreExplanation(content);
  }
}

export function resolveAuthenticEditorialTheme(title: string, body: string): StimulusTheme | undefined {
  const blob = `${title} ${body}`;
  const candidates: StimulusTheme[] = ['letter', 'newspaper', 'poem', 'document'];
  for (const theme of candidates) {
    if (isAuthenticGenreSample(body, theme)) return theme;
  }
  const inferred = inferStimulusTheme('prose', body, title);
  if (inferred !== 'prose' && isAuthenticGenreSample(body, inferred)) return inferred;
  return undefined;
}

function sanitizeThemedDestacado<T extends GuideBlockLike>(block: T): T {
  const payload = { ...(block.payload as Record<string, unknown>) };
  const body = String(payload.body || '').trim();
  const title = String(payload.title || block.label || '').trim();
  const theme = String(payload.editorialTheme || '').trim() as StimulusTheme;

  if (theme && theme !== 'prose' && !isAuthenticGenreSample(body, theme)) {
    const { editorialTheme: _removed, ...rest } = payload;
    return { ...block, payload: rest };
  }

  if (!theme || theme === 'prose') {
    const authentic = resolveAuthenticEditorialTheme(title, body);
    if (authentic) {
      return {
        ...block,
        payload: { ...payload, tone: 'neutral', editorialTheme: authentic },
      };
    }
  }

  return block;
}

function sanitizeStimulusObject(stimulus: Record<string, unknown>): Record<string, unknown> {
  const content = String(stimulus.content || '').trim();
  const theme = String(stimulus.theme || '').trim() as StimulusTheme;
  const type = String(stimulus.type || 'prose');

  if (!content) return stimulus;

  if (isTheoreticalGenreExplanation(content)) {
    const { theme: _t, ...rest } = stimulus;
    return { ...rest, type: type === 'general' ? 'prose' : type };
  }

  if (theme && theme !== 'prose' && !isAuthenticGenreSample(content, theme)) {
    const { theme: _t, ...rest } = stimulus;
    return rest;
  }

  const authenticTheme = resolveAuthenticEditorialTheme(String(stimulus.title || ''), content);
  if (authenticTheme) {
    return { ...stimulus, theme: authenticTheme };
  }

  return stimulus;
}

export function enforceGenreShowDontTell<T extends GuideBlockLike>(
  blocks: T[],
  subjectTag?: string,
): T[] {
  if (!isHumanitiesFormulaSubject(subjectTag)) return blocks;

  return blocks.map((block) => {
    if (block.mode === 'destacado') {
      return sanitizeThemedDestacado(block);
    }

    const payload = { ...(block.payload as Record<string, unknown>) };
    const stimulus = payload.stimulus;
    if (!stimulus || typeof stimulus !== 'object') return block;

    const nextStimulus = sanitizeStimulusObject(stimulus as Record<string, unknown>);
    if (nextStimulus === stimulus) return block;
    return { ...block, payload: { ...payload, stimulus: nextStimulus } };
  });
}
